/**
 * contentEditableUtils.ts — Shared pure DOM helpers for contentEditable
 * text editors with inline mention chips.
 *
 * Extracted from RichTextBox.tsx to share across ChatInput,
 * InlineMentionComposer, and RichTextBox itself.
 */

import React, { memo, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { getThumbnailUrl } from '@ponpon/api-client'
import type { MentionCandidate } from '../canvas-v2/nodes/shared/mentionUtils'
import { NODE_TYPE_ICON, NODE_TYPE_ICON_SVG } from '../canvas-v2/nodes/shared/nodeTypeIcons'

// Re-export MentionCandidate for convenience
export type { MentionCandidate }

// ─── Pure DOM helpers ───────────────────────────────────────────────────────

/**
 * Create a mention chip `<span contentEditable="false">` element.
 * Includes optional thumbnail image, icon, label, and a remove button.
 */
export function createMentionChipElement(
  candidate: MentionCandidate,
  projectId?: string,
): HTMLSpanElement {
  const chip = document.createElement('span')
  chip.contentEditable = 'false'
  chip.dataset.mentionPill = 'true'
  chip.dataset.nodeId = candidate.id
  chip.dataset.label = candidate.label
  chip.dataset.icon = candidate.icon
  chip.className =
    'inline-flex items-center gap-1 rounded-md border border-primary/25 bg-primary/15 px-1.5 py-0.5 text-xs text-primary align-baseline mx-0.5'
  chip.setAttribute('data-testid', `mention-chip-${candidate.id}`)

  // Thumbnail (if available)
  if (candidate.thumbnailPath && projectId) {
    const thumb = document.createElement('img')
    thumb.src = getThumbnailUrl(projectId, candidate.thumbnailPath, 0) ?? ''
    thumb.alt = ''
    thumb.className = 'w-3.5 h-3.5 rounded-sm object-cover flex-shrink-0 pointer-events-none'
    thumb.onerror = () => {
      thumb.style.display = 'none'
    }
    chip.appendChild(thumb)
  }

  // Label (clickable)
  const label = document.createElement('span')
  label.dataset.mentionChipLabel = 'true'
  label.className = 'inline-flex items-center gap-0.5 cursor-pointer hover:underline'
  label.setAttribute('data-testid', `mention-chip-label-${candidate.id}`)

  const icon = document.createElement('span')
  icon.className = 'pointer-events-none flex-shrink-0'
  const svgMarkup = NODE_TYPE_ICON_SVG[candidate.type]
  if (svgMarkup) {
    icon.innerHTML = svgMarkup
    icon.style.width = '12px'
    icon.style.height = '12px'
    icon.style.display = 'inline-flex'
    const svgEl = icon.querySelector('svg')
    if (svgEl) {
      svgEl.style.width = '100%'
      svgEl.style.height = '100%'
      svgEl.style.color = 'rgba(168, 132, 72, 0.7)'
    }
  } else {
    icon.className = 'pointer-events-none text-[11px]'
    icon.textContent = candidate.icon
  }

  const text = document.createElement('span')
  text.className = 'pointer-events-none'
  text.textContent = candidate.label

  label.append(icon, text)

  // Remove button
  const remove = document.createElement('button')
  remove.type = 'button'
  remove.contentEditable = 'false'
  remove.dataset.mentionChipRemove = 'true'
  remove.className = 'ml-0.5 text-primary/60 transition-colors hover:text-primary leading-none'
  remove.title = 'Remove reference'
  remove.setAttribute('aria-label', `Remove ${candidate.label} reference`)
  remove.setAttribute('data-testid', `mention-chip-remove-${candidate.id}`)
  remove.textContent = '\u00d7'

  chip.append(label, remove)
  return chip
}

/**
 * Recursively serialize a DOM node tree into plain text,
 * converting mention chips to `@[[nodeId]]` markers.
 */
export function serializeNodeText(node: globalThis.Node, root: HTMLElement): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? ''
  }
  if (!(node instanceof HTMLElement)) return ''

  const element = node
  if (element.dataset.mentionPill === 'true') {
    const nodeId = element.dataset.nodeId
    return nodeId ? `@[[${nodeId}]]` : ''
  }
  if (element.tagName === 'BR') return '\n'

  const text = Array.from(node.childNodes)
    .map((child) => serializeNodeText(child, root))
    .join('')

  if (element !== root && (element.tagName === 'DIV' || element.tagName === 'P')) {
    return text.endsWith('\n') ? text : `${text}\n`
  }
  return text
}

/**
 * Normalize whitespace and NBSP in plain text.
 */
export function normalizePlainText(text: string): string {
  return text
    .replace(/\u00A0/g, ' ')
    .replace(/[^\S\r\n]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Get normalized plain text from a contentEditable editor element.
 */
export function getPlainTextFromEditor(editor: HTMLElement | null): string {
  if (!editor) return ''
  return normalizePlainText(serializeNodeText(editor, editor))
}

/**
 * Get deduplicated, ordered node IDs from all mention chips in the editor.
 */
export function getReferencedNodeIdsFromEditor(editor: HTMLElement | null): string[] {
  if (!editor) return []
  const seen = new Set<string>()
  const ordered: string[] = []
  editor.querySelectorAll<HTMLElement>('[data-mention-pill]').forEach((chip) => {
    const nodeId = chip.dataset.nodeId
    if (nodeId && !seen.has(nodeId)) {
      seen.add(nodeId)
      ordered.push(nodeId)
    }
  })
  return ordered
}

/**
 * Check whether the current selection anchor is inside the editor element.
 */
export function selectionInsideEditor(
  editor: HTMLElement,
  selection: Selection | null,
): boolean {
  if (!selection || selection.rangeCount === 0) return false
  return editor.contains(selection.anchorNode)
}

/**
 * Place the caret at the end of a contentEditable element and focus it.
 */
export function placeCaretAtEnd(editor: HTMLElement) {
  const selection = window.getSelection()
  if (!selection) return
  const range = document.createRange()
  range.selectNodeContents(editor)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
  editor.focus()
}

/**
 * Hydrate raw text with `@[[nodeId]]` markers into mention chip elements.
 * Unknown node IDs get fallback chips with muted style.
 */
export function hydrateContent(
  editor: HTMLElement,
  content: string,
  candidates: MentionCandidate[],
  projectId?: string,
) {
  const MARKER_RE = /@\[\[([^\]]+)\]\]/g
  const fragment = document.createDocumentFragment()
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = MARKER_RE.exec(content)) !== null) {
    if (match.index > lastIndex) {
      fragment.appendChild(
        document.createTextNode(content.slice(lastIndex, match.index)),
      )
    }
    const nodeId = match[1]
    const candidate = candidates.find((c) => c.id === nodeId)
    if (candidate) {
      fragment.appendChild(createMentionChipElement(candidate, projectId))
    } else {
      // Unknown node -- render fallback chip with muted style
      const fallback: MentionCandidate = {
        id: nodeId,
        type: 'unknown',
        label: nodeId.slice(0, 8) + '...',
        icon: '',
      }
      const chip = createMentionChipElement(fallback, projectId)
      chip.className = chip.className
        .replace('border-primary/25', 'border-white/10')
        .replace('bg-primary/15', 'bg-white/5')
        .replace('text-primary', 'text-foreground/40')
      chip.style.textDecoration = 'line-through'
      fragment.appendChild(chip)
    }
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < content.length) {
    fragment.appendChild(document.createTextNode(content.slice(lastIndex)))
  }

  editor.innerHTML = ''
  editor.appendChild(fragment)
}

// ─── DropdownItem React Component ───────────────────────────────────────────

export interface DropdownItemProps {
  candidate: MentionCandidate
  isHighlighted: boolean
  projectId?: string
  onSelect: (candidate: MentionCandidate) => void
  onMouseEnter: () => void
}

export const DropdownItem = memo(function DropdownItem({
  candidate,
  isHighlighted,
  projectId,
  onSelect,
  onMouseEnter,
}: DropdownItemProps) {
  return React.createElement(
    'div',
    {
      'data-mention-item': true,
      role: 'option',
      'aria-selected': isHighlighted,
      className: `flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors ${
        isHighlighted ? 'bg-primary/15' : 'hover:bg-white/5'
      }`,
      onMouseDown: (e: React.MouseEvent) => {
        e.preventDefault()
        onSelect(candidate)
      },
      onMouseEnter,
    },
    // Thumbnail or SVG icon fallback
    candidate.thumbnailPath && projectId
      ? React.createElement('img', {
          src: getThumbnailUrl(projectId, candidate.thumbnailPath, 0) ?? undefined,
          alt: '',
          className: 'w-6 h-6 rounded-sm object-cover flex-shrink-0 bg-black/20',
        })
      : React.createElement(
          'span',
          {
            className: 'w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground/50',
          },
          NODE_TYPE_ICON[candidate.type] || candidate.icon || '\u{1F4E6}',
        ),
    // SVG type icon
    React.createElement(
      'span',
      { className: 'w-3.5 h-3.5 flex-shrink-0 text-foreground/50' },
      NODE_TYPE_ICON[candidate.type] || React.createElement('span', { className: 'text-sm' }, candidate.icon),
    ),
    // Label
    React.createElement(
      'span',
      { className: 'flex-1 truncate text-foreground' },
      candidate.label,
    ),
    // Type
    React.createElement(
      'span',
      { className: 'flex-shrink-0 text-xs italic text-text-muted' },
      candidate.type,
    ),
  )
})
DropdownItem.displayName = 'DropdownItem'

// ─── Portal-based Mention Dropdown ──────────────────────────────────────────

export interface MentionDropdownPortalProps {
  candidates: MentionCandidate[]
  highlightIndex: number
  projectId?: string
  editorRef: React.RefObject<HTMLElement | null>
  dropdownRef?: React.RefObject<HTMLDivElement | null>
  onSelect: (candidate: MentionCandidate) => void
  onHighlight: (index: number) => void
}

/**
 * Portal-based mention dropdown rendered to `document.body`.
 * Positions itself above the editor element using getBoundingClientRect.
 */
export const MentionDropdownPortal = memo(function MentionDropdownPortal({
  candidates,
  highlightIndex,
  projectId,
  editorRef,
  dropdownRef: externalDropdownRef,
  onSelect,
  onHighlight,
}: MentionDropdownPortalProps) {
  const [editorRect, setEditorRect] = useState<DOMRect | null>(null)

  useLayoutEffect(() => {
    const el = editorRef.current
    if (!el || candidates.length === 0) {
      setEditorRect(null)
      return
    }
    setEditorRect(el.getBoundingClientRect())
  }, [editorRef, candidates.length])

  if (candidates.length === 0 || !editorRect) return null

  const dropdown = React.createElement(
    'div',
    {
      ref: externalDropdownRef,
      id: 'chat-mention-dropdown',
      className:
        'fixed z-[9999] max-h-[200px] overflow-y-auto rounded-lg border border-border-subtle bg-surface-1 shadow-lg',
      style: {
        top: editorRect.top - 4,
        left: editorRect.left,
        width: editorRect.width,
        transform: 'translateY(-100%)',
      },
      role: 'listbox',
      'data-testid': 'mention-dropdown',
    },
    candidates.map((candidate, index) =>
      React.createElement(DropdownItem, {
        key: candidate.id,
        candidate,
        isHighlighted: index === highlightIndex,
        projectId,
        onSelect,
        onMouseEnter: () => onHighlight(index),
      }),
    ),
  )

  return createPortal(dropdown, document.body)
})
MentionDropdownPortal.displayName = 'MentionDropdownPortal'
